<!-- 
  File: src/components/VideoPlayer.vue
  Description: 影像播放器 (By video.js & mpegts.js)
  Author: Eason Wang
  Created: 2025-05-06
  Last Modified: 2025-08-22
-->

<template>
  <v-responsive
    class="ma-auto"
    :aspect-ratio="aspectRatio"
    :max-height="maxHeight"
    :max-width="maxWidth"
  >
    <div class="video-zoom-container" ref="zoomContainer">
      <video
        :id="id"
        class="video-js vjs-default-skin"
        :style="techStyle"
        crossorigin="anonymous"
      >
        <source :src="src" :type="type" />
        <p class="vjs-no-js">
          要觀看此視頻，請啟用 JavaScript，並考慮升級到支援 HTML5 影片的瀏覽器。
        </p>
      </video>

      <!-- 框選區域 -->
      <div v-if="selecting" class="selection-box" :style="selectionStyle"></div>
    </div>
  </v-responsive>
</template>

<script>
import videojs from "video.js";
import mpegts from "mpegts.js";

export default {
  name: 'VideoPlayer',
  props: {
    /**
     * 影像來源路徑
     */
    id: {
      type: String,
      default: "video-player",
    },
    /**
     * 影像類別
     *
     * HLS: 'application/x-mpegURL', FLV: 'video/x-flv', RTMP: 'rtmp/flv', MP4: 'video/mp4',
     * TS(.ts): 'mpegts' (H264 編碼透過 mpegts.js 可以；MPEG2 編碼確認目前瀏覽器無法播放).
     */
    type: {
      type: String,
      default: "application/x-mpegURL",
    },
    /**
     * 影像來源路徑
     */
    src: {
      type: String,
      default: "",
    },
    /**
     * 畫面長寬比
     *
     * 預設 1920/1080
     */
    aspectRatio: {
      type: Number,
      default: 1.7778,
    },
    /**
     * 最大寬度
     */
    maxWidth: {
      type: [String, Number],
      default: 600,
    },
    /**
     * 最大高度
     */
    maxHeight: {
      type: [String, Number],
      default: 800,
    },
    /**
     * 是否顯示控制項目
     */
    controls: {
      type: Boolean,
      default: true,
    },
    /**
     * 是否自動播放
     */
    autoplay:{
      type: Boolean,
      default: true,
    },
    /**
     * 是否預加載
     */
    preload: {
      type: Boolean,
      default: false,
    },
    /**
     * token
     */
    token: {
      type: String,
      default: "",
    }
  },
  data() {
    return {
      player: null,
      FitMode: {
        Original:'',
        Frame:''
      },
      /**
       * 顯示模式：'original'（維持比例）或 'frame'（依外框比例）
       */
      fitMode: '',

      tsPlayer: null,
      lastX: 0,
      lastY:0, 
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      volume: 0.5,
      muted: true,
      rate: 1,

      zoomEnabled: false,
      zoomContainer: null,
      videoEl: null,
      selecting: false,
      selection: { x: 0, y: 0, w: 0, h: 0 },
      dragging: false,

    }
  },
  computed: {
    techStyle () {
      return {
        objectFit: this.fitMode === this.FitMode.Original ? "contain" : "fill", // contain 原始比例；fill 貼滿外框（若想貼滿不變形用 'cover'，但會裁邊）
        width: "100%",
        height: "100%",
      }
    },
    /**
     * 電子放大框選樣式
     */
    selectionStyle () {
      return {
        left: this.selection.x + "px",
        top: this.selection.y + "px",
        width: this.selection.w + "px",
        height: this.selection.h + "px",
      }
    },
  },
  mounted() {
    
    console.info("[VideoPlayer]", this.id, this.type, this.src);
    this.fitMode= this.FitMode.Original;

    // 延遲載入 videojs
    this.$nextTick(() => {
      const playerOption = {
        preload: this.preload ? "auto" : "", // 預加載
        autoplay: this.autoplay, // 自動播放 (為了使 html 5 元素上的自動播放在 chrome 66 更新後正常運作，您只需將 muted="muted" 屬性新增至影片元素即可。)
        muted: this.autoplay ? "muted" : "", // 自動靜音，使 html 5 元素上的自動播放在 chrome 66 更新後正常運作
        controls: this.controls, // 顯示控制項目
      };

      videojs.Vhs.xhr.onRequest =  (options) => {
        let headers = options.headers || {};
        if (this.token) {
          headers["Authorization"] = `Bearer ${this.token}`;
        }

        options.headers = headers;
        return options;
      };

      this.player = videojs(this.id, playerOption, () => {
        console.log("[VideoPlayer]", this.id, "ready");
      });

      // 資料變化監聽 (可能是透過原控制項目調整)
      this.player.on("timeupdate", () => {
        this.currentTime = this.player.currentTime();
        this.duration = this.player.duration();
      });
      this.player.on("play", () => (this.isPlaying = true));
      this.player.on("pause", () => (this.isPlaying = false));
      this.player.on("volumechange", () => {
        console.debug("[VideoPlayer] Volume change", this.player.volume(), this.player.muted());
        this.volume = this.player.volume();
        this.$emit("volume-change", this.volume);

        this.muted = this.player.muted();
        this.$emit("muted-change", this.muted);
      });
      this.player.on("ended", () => this.$emit("ended"));


      this.player.on('xhr-hooks-ready', () => {
      })

    });
  },
  // 所有綁定、事件監聽、Watch與渲染至目標的HTML DOM…等等皆移除時
  onUnmounted(){
    // 銷毀 video player
    if (this.player) this.player.dispose();
  },
  beforeDestroy() {
    if (this.player) {
      this.player.dispose();
    }
  },
  methods:{
    

    /**
     * 初始化與重置 (停止播放影片呈現黑畫面)
     */
    reset() {
      console.log("[VideoPlayer] Reset");
      
      if (this.player) {
        this.player.pause(); // 必須要加此，以免下次 reset 播放有問題
        this.player.src([]);
        this.player.reset();
        this.player.error(null);
      }
    },
    /**
     * 播放新的一部
     * @param {String} src 影像來源路徑
     * @param {String} type 影像類別 HLS: 'application/x-mpegURL', FLV: 'video/x-flv', RTMP: 'rtmp/flv', MP4: 'video/mp4', TS(.ts): 'mpegts'
     */
    playNew(src, type = "", autoplay = true) {
      if (this.player) {
        console.log("[VideoPlayer] Play new", type);

        if (type == "mpegts") {
          // H264 TS 檔案播放 by mpegts.js

          const videoEl = this.player.el().querySelector("video");
          if (!videoEl) {
            console.error("[VideoPlayer] <video> not found");
            return;
          }

          this.tsPlayer = mpegts.createPlayer(
            {
              type: "mpegts",
              isLive: false, // 串流或影片檔
              url: src,
            },
            {
              enableStashBuffer: true,
            }
          );

          this.tsPlayer.attachMediaElement(videoEl);
          this.tsPlayer.load();
          console.debug("[VideoPlayer] Play new mpegts url:", src);
        } else {
          // Other by video.js

          this.player.src({
            src: src,
            type: type,
          });
          this.player.load();
          console.debug("[VideoPlayer] Play new videojs url:", src);
        }

        // 自動播放
        setTimeout(() => {
          if (autoplay) {
            this.play();
          } else {
            // 不自動播放
            this.pause();
          }
        }, 10);
      }
    },

    /**
     * 全螢幕
     */
    fullscreen() {
      console.log("[VideoPlayer]", this.id, "request fullscreen");
      if (this.player) this.player.requestFullscreen();
    },

    /**
     * 截圖
     */
    screenshot() {
      if (this.player) {
        const fileName = `${new Date().getTime()}.png`;
        let fileType = "png";
        console.log("[VideoPlayer]", this.id, "screenshot", fileName);

        const video = this.player.el().querySelector("video");
        const canvas = (window.canvas = document.createElement("canvas"));
        canvas.width = video.videoWidth; //video.offsetWidth;
        canvas.height = video.videoHeight; //video.offsetHeight;
        canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height); // 圖片大小和影片解析度一致
        const strDataURL = canvas.toDataURL("image/" + fileType); // canvas中video中取一幀圖片並轉成dataURL
        const arr = strDataURL.split(",");
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = window.atob(arr[1]);
        let n = bstr.length;
        let u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        const blob = new Blob([u8arr], {
          type: mime,
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        setTimeout(function () {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 1000);
      }
    },
      

    /**
     * 播放 / 暫停
     */
    togglePlay() {
      if (this.player.paused()) this.play();
      else this.pause();
    },

    /**
     * 播放
     */
    play() {
      if (this.player && this.src.length > 0) {
        this.player.play();
      } else {
        console.log(
          "[VideoPlayer] The video is not playing because the video is not in good condition yet."
        );
      }
    },

    /**
     * 暫停
     */
    pause() {
      if (this.player) {
        this.player.pause();
      }
    },

    /**
     * 播放速度調整
     */
    setPlaybackRate(val) {
      if (val !== undefined && val !== null) this.rate = val;
      if (this.player) {
        this.player.playbackRate(this.rate);
      }
    },

    /**
     * 時間軸控制
     */
    seekVideo(val) {
      if (val !== undefined && val !== null) this.currentTime = val;
      if (this.player) {
        this.player.currentTime(this.currentTime);
      }
    },

    /**
     * 靜音切換
     */
    toggleMute() {
      this.muted = !this.muted;
      if (this.player) {
        this.player.muted(this.muted);
      }
    },

    /**
     * 音量控制
     */
    setVolume(val) {
      if (val !== undefined && val !== null) this.volume = val;
      if (this.player) {
        this.player.volume(this.volume);

        if (this.volume == 0) this.player.muted(true);
      }
    },

    /**
     * 保持原畫面顯示比例
     */
    keepOriginalAspect() {
      this.fitMode = this.FitMode.Original; // contain
    },

  /**************************************************
   *                    電子放大
   **************************************************/

    /**
     * 根據視窗適應顯示比例
     */
    fitToFrame() {
      this.fitMode = this.FitMode.Frame; // fill（若想貼滿不變形用 'cover'，但會裁邊）
    },
    /**
     * 一般固定中間放大 1->2, 2->1
     */
    toggleZoom(size = 2) {
      if (this.player) {
        this.zoomEnabled = !this.zoomEnabled;
        this.videoEl = document.getElementById(this.id);
        if (this.videoEl) {
          this.videoEl.style.transform = this.zoomEnabled
            ? "scale(" + size + ")"
            : "scale(1)";
          this.videoEl.style.transformOrigin = "center";
        }
      }
    },
    /**
     * 切換電子放大模式
     */
    toggleElectronicZoom() {
      this.videoEl = document.getElementById(this.id);
      if (this.zoomEnabled) {
        this.resetZoom();
      } else {
        this.enableSelection();
      }
    },

    /**
     * 開始電子放大模式
     */
    enableSelection() {
      if (!this.zoomContainer) return;
      this.zoomEnabled = true;
      console.log("[VideoPlayer] EnableSelection");

      this.zoomContainer.onmousedown = (e) => {
        // 如果已經放大，禁止再次框選
        if (
          !this.zoomEnabled ||
          (this.videoEl?.style.transform !== "none" && this.videoEl?.style.transform !== "")
        )
          return;
        console.debug("[VideoPlayer] 框選中");

        const rect = this.zoomContainer.getBoundingClientRect();
        const startX = e.clientX - rect.left;
        const startY = e.clientY - rect.top;

        this.selecting = true;
        this.selection = { x: startX, y: startY, w: 0, h: 0 };

        const onMouseMove = (moveEvent) => {
          const currentX = moveEvent.clientX - rect.left;
          const currentY = moveEvent.clientY - rect.top;

          this.selection.w = Math.abs(currentX - startX);
          this.selection.h = Math.abs(currentY - startY);
          this.selection.x = Math.min(startX, currentX);
          this.selection.y = Math.min(startY, currentY);
        };

        const onMouseUp = () => {
          console.log("[VideoPlayer] MouseUp");

          this.selecting = false;
          document.removeEventListener("mousemove", onMouseMove);
          document.removeEventListener("mouseup", onMouseUp);
          this.applyZoom();
        };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
      };
    },
    /**
     * 套用放大
     */
    applyZoom() {
      if (!this.videoEl || this.selection.w <= 0 || this.selection.h <= 0) return;
      console.log("[VideoPlayer] ApplyZoom");

      const container = this.zoomContainer;
      const video = this.videoEl;

      // 計算縮放比例
      const scaleX = container.clientWidth / this.selection.w;
      const scaleY = container.clientHeight / this.selection.h;
      const scale = Math.min(scaleX, scaleY); // 保持比例，不變形

      // 容器與選取框的中心
      const containerCenterX = container.clientWidth / 2;
      const containerCenterY = container.clientHeight / 2;
      const selectionCenterX = this.selection.x + this.selection.w / 2;
      const selectionCenterY = this.selection.y + this.selection.h / 2;

      // 位移計算，讓選取框中心對準容器中心
      const offsetX = containerCenterX - selectionCenterX * scale;
      const offsetY = containerCenterY - selectionCenterY * scale;

      video.style.transition = "transform 0.3s ease";
      video.style.transformOrigin = "0 0";
      video.style.transform = `translate(${offsetX}px, ${offsetY}px) scale(${scale})`;

      // 支援拖曳移動
      this.videoEl.onmousedown = this.startDrag;
    },
    /**
     * 電子放大開始拖曳移動
     */
    startDrag(e) {
      if (!this.videoEl) return;
      this.dragging = true;
      this.lastX = e.clientX;
      this.lastY = e.clientY;

      const video = this.videoEl;
      const style = window.getComputedStyle(video);
      const matrix = new DOMMatrixReadOnly(style.transform);
      let tx = matrix.m41;
      let ty = matrix.m42;
      const scale = matrix.a;

      const onMouseMove = (moveEvent) => {
        if (!this.dragging) return;
        const dx = moveEvent.clientX - this.lastX;
        const dy = moveEvent.clientY - this.lastY;
        this.lastX = moveEvent.clientX;
        this.lastY = moveEvent.clientY;
        tx += dx;
        ty += dy;
        video.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
      };

      const onMouseUp = () => {
        this.dragging = false;
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    /**
     * 還原電子放大
     */
    resetZoom() {
      console.log("[VideoPlayer] ResetZoom");
      this.zoomEnabled = false;
      if (this.videoEl) {
        this.videoEl.style.transform = "none";

        this.videoEl.onmousedown = null;
      }
    }
  }
}
</script>

<style scoped>
/* 填滿父物件畫面 */
.video-js {
  width: 100%;
  height: 100%;
}
/* 放大功能容器(遮罩) */
.video-zoom-container {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
}
/* 放大選擇框 */
.selection-box {
  position: absolute;
  border: 2px dashed #42a5f5;
  background: rgba(66, 165, 245, 0.2);
}
</style>
